(() => {
  const VALID_MODES = new Set(['local', 'shipping']);

  const STALE_FULFILLMENT_ATTRIBUTES = [
    'Fulfillment Category',
    'Fulfillment Type',
    'Fulfillment Option Handle',
    'Fulfillment Option Name',
    'Pickup Event ID',
    'Pickup Date',
    'Pickup Day',
    'Pickup Location',
    'Pickup City State',
    'Pickup Address',
    'Pickup Window',
    'Pickup Instructions',
    'Delivery Event ID',
    'Delivery Date',
    'Delivery Day',
    'Delivery Area',
    'Delivery Window',
    'Delivery Zip Code',
    'Delivery Instructions',
    'Shipping Event ID',
    'Shipping Option',
    'Requested Delivery Date',
    'Requested Delivery Day',
    'Estimated Ship Date',
    'Shipping Method',
    'Checkout Method To Select',
    'Shopping Path',
    'Porch Live Purchase',
    'Thursday Preorder Close Time',
    'Thursday Live Open Time',
    'Thursday Live Close Time',
    'Self Serve Purchase',
    'Self Serve Open Time',
    'Self Serve Close Time',
  ];

  class ColdSugarOrderModeController {
    constructor() {
      this.dialog = document.querySelector('[data-cs-global-mode-dialog]');
      this.title = this.dialog?.querySelector('[data-cs-global-dialog-title]');
      this.message = this.dialog?.querySelector('[data-cs-global-dialog-message]');
      this.error = this.dialog?.querySelector('[data-cs-global-dialog-error]');
      this.keepButton = this.dialog?.querySelector('[data-cs-global-dialog-keep]');
      this.switchButton = this.dialog?.querySelector('[data-cs-global-dialog-switch]');
      this.closeButton = this.dialog?.querySelector('[data-cs-global-dialog-close]');

      this.pendingResolve = null;
      this.lastFocusedElement = null;
      this.isSwitching = false;

      this.bindDialog();
    }

    normalizeMode(value) {
      const mode = String(value || '').trim().toLowerCase();
      return VALID_MODES.has(mode) ? mode : '';
    }

    async beforeAdd({ orderMode, trigger = null } = {}) {
      const requestedMode = this.normalizeMode(orderMode);

      if (!requestedMode) {
        throw new Error(
          'This product is temporarily unavailable because its order type has not been configured.'
        );
      }

      let cart = await this.fetchCart();

      const guardedCart =
        await this.guardExpiredPorchCart({
          cart,
          trigger,
        });

      if (!guardedCart.allowed) {
        return false;
      }

      cart = guardedCart.cart;

      const cartMode =
        this.determineCartMode(cart);

      if (
        cartMode === 'empty' ||
        cartMode === requestedMode
      ) {
        return true;
      }

      const shouldSwitch =
        await this.requestModeSwitch({
          cartMode,
          requestedMode,
          trigger,
        });

      if (!shouldSwitch) {
        return false;
      }

      await this.clearCartAndFulfillment();
      return true;
    }

    async beforeAnyAdd({
      trigger = null,
    } = {}) {
      const guardedCart =
        await this.guardExpiredPorchCart({
          trigger,
        });

      return guardedCart.allowed;
    }

    async guardExpiredPorchCart({
      cart = null,
      trigger = null,
    } = {}) {
      const currentCart =
        cart || await this.fetchCart();

      const expired =
        this.findExpiredPorchItems(
          currentCart
        );

      if (!expired.items.length) {
        return {
          allowed: true,
          cart: currentCart,
        };
      }

      const shouldRemove =
        await this.requestExpiredPorchCleanup({
          expired,
          trigger,
        });

      if (!shouldRemove) {
        return {
          allowed: false,
          cart: currentCart,
        };
      }

      const updatedCart =
        await this.removeExpiredPorchItems(
          currentCart,
          expired
        );

      return {
        allowed: true,
        cart: updatedCart,
      };
    }

    normalizeShoppingPath(value) {
      return String(value || '')
        .trim()
        .toLowerCase();
    }

    isPorchPath(value) {
      return (
        this.normalizeShoppingPath(value) ===
        'porch_live'
      );
    }

    getCartPorchEventDate(cart) {
      const attributes =
        cart?.attributes || {};

      const lineWithEvent =
        (cart?.items || []).find((item) => {
          if (this.isPickupLock(item)) {
            return false;
          }

          const properties =
            item?.properties || {};

          const path =
            this.normalizeShoppingPath(
              properties
                ._cold_sugar_shopping_path
            );

          return (
            this.isPorchPath(path) &&
            /^\d{4}-\d{2}-\d{2}$/.test(
              String(
                properties
                  ._cold_sugar_porch_event ||
                ''
              )
            )
          );
        });

      if (lineWithEvent) {
        return String(
          lineWithEvent.properties
            ._cold_sugar_porch_event
        );
      }

      const eventId = String(
        attributes['Pickup Event ID'] || ''
      );

      const eventMatch =
        eventId.match(
          /(\d{4}-\d{2}-\d{2})/
        );

      if (eventMatch) {
        return eventMatch[1];
      }

      const pickupDate = String(
        attributes['Pickup Date'] || ''
      ).trim();

      if (pickupDate) {
        const parsed =
          new Date(`${pickupDate} 12:00:00`);

        if (
          Number.isFinite(parsed.getTime())
        ) {
          return [
            parsed.getFullYear(),
            String(
              parsed.getMonth() + 1
            ).padStart(2, '0'),
            String(
              parsed.getDate()
            ).padStart(2, '0'),
          ].join('-');
        }
      }

      return '';
    }

    getCentralClock() {
      const parts =
        new Intl.DateTimeFormat(
          'en-US',
          {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
          }
        ).formatToParts(new Date());

      const values = {};

      parts.forEach((part) => {
        if (part.type !== 'literal') {
          values[part.type] =
            Number(part.value);
        }
      });

      return {
        date: [
          values.year,
          String(values.month).padStart(
            2,
            '0'
          ),
          String(values.day).padStart(
            2,
            '0'
          ),
        ].join('-'),
        minutes:
          Number(values.hour || 0) * 60 +
          Number(values.minute || 0),
      };
    }

    isExpiredPorchPreviewForced() {
      const params =
        new URLSearchParams(
          window.location.search
        );

      const themeRole = String(
        window.Shopify?.theme?.role || ''
      ).toLowerCase();

      const isThemePreview = Boolean(
        window.Shopify?.designMode ||
        themeRole === 'unpublished' ||
        themeRole === 'development' ||
        [
          '127.0.0.1',
          'localhost',
        ].includes(
          window.location.hostname
        )
      );

      if (!isThemePreview) {
        return false;
      }

      return (
        params.get(
          'preview_expired_porch'
        ) === '1' ||
        params.get('preview_state') ===
          'ended'
      );
    }

    isPorchEventExpired(
      eventDate,
      closeTime
    ) {
      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(
          String(eventDate || '')
        )
      ) {
        return false;
      }

      const clock =
        this.getCentralClock();

      if (eventDate < clock.date) {
        return true;
      }

      if (eventDate > clock.date) {
        return false;
      }

      const timeMatch =
        String(closeTime || '').match(
          /^(\d{1,2}):(\d{2})$/
        );

      const closeMinutes = timeMatch
        ? (
            Number(timeMatch[1]) * 60 +
            Number(timeMatch[2])
          )
        : 19 * 60;

      return (
        clock.minutes >= closeMinutes
      );
    }

    findExpiredPorchItems(cart) {
      const attributes =
        cart?.attributes || {};

      const cartPath =
        this.normalizeShoppingPath(
          attributes['Shopping Path']
        );

      const fallbackEventDate =
        this.getCartPorchEventDate(cart);

      const closeTime = String(
        attributes[
          'Thursday Live Close Time'
        ] || '19:00'
      ).trim();

      const forceExpired =
        this.isExpiredPorchPreviewForced();

      const porchRecords = [];
      const expiredRecords = [];

      (cart?.items || []).forEach(
        (item) => {
          if (this.isPickupLock(item)) {
            return;
          }

          const properties =
            item?.properties || {};

          let itemPath =
            this.normalizeShoppingPath(
              properties
                ._cold_sugar_shopping_path
            );

          if (
            !itemPath &&
            this.isPorchPath(cartPath)
          ) {
            itemPath = cartPath;
          }

          if (
            !this.isPorchPath(itemPath)
          ) {
            return;
          }

          const eventDate = String(
            properties
              ._cold_sugar_porch_event ||
            fallbackEventDate ||
            ''
          ).trim();

          const record = {
            item,
            path: itemPath,
            eventDate,
          };

          porchRecords.push(record);

          if (
            forceExpired ||
            this.isPorchEventExpired(
              eventDate,
              closeTime
            )
          ) {
            expiredRecords.push(record);
          }
        }
      );

      const expiredKeys = new Set(
        expiredRecords.map(
          (record) => record.item.key
        )
      );

      return {
        items: expiredRecords.map(
          (record) => record.item
        ),
        records: expiredRecords,
        remainingPorchItems:
          porchRecords.filter(
            (record) =>
              !expiredKeys.has(
                record.item.key
              )
          ),
        eventDates: [
          ...new Set(
            expiredRecords
              .map(
                (record) =>
                  record.eventDate
              )
              .filter(Boolean)
          ),
        ],
        cartPath,
      };
    }

    formatPorchEventDate(value) {
      const match = String(
        value || ''
      ).match(
        /^(\d{4})-(\d{2})-(\d{2})$/
      );

      if (!match) {
        return '';
      }

      const date = new Date(
        Date.UTC(
          Number(match[1]),
          Number(match[2]) - 1,
          Number(match[3])
        )
      );

      return new Intl.DateTimeFormat(
        'en-US',
        {
          timeZone: 'UTC',
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        }
      ).format(date);
    }

    requestExpiredPorchCleanup({
      expired,
      trigger,
    }) {
      const firstDate =
        expired.eventDates[0] || '';

      const dateLabel =
        this.formatPorchEventDate(
          firstDate
        );

      const itemCount =
        expired.items.reduce(
          (total, item) =>
            total +
            Number(item.quantity || 0),
          0
        );

      const itemLabel =
        itemCount === 1
          ? 'item'
          : 'items';

      const message = dateLabel
        ? (
            `Your cart contains ${itemCount} ${itemLabel} from Porch Pickup on ${dateLabel}. ` +
            'That event has closed, so those items can no longer be purchased. ' +
            'To continue, we’ll remove the expired Porch Pickup items.'
          )
        : (
            'Your cart contains items from a Porch Pickup event that has already closed. ' +
            'Those items can no longer be purchased. ' +
            'To continue, we’ll remove the expired Porch Pickup items.'
          );

      if (!this.dialog) {
        return Promise.resolve(
          window.confirm(
            `${message}\n\nRemove the expired items and continue?`
          )
        );
      }

      if (this.pendingResolve) {
        this.finishDecision(false);
      }

      this.lastFocusedElement =
        trigger instanceof HTMLElement
          ? trigger
          : document.activeElement;

      if (this.title) {
        this.title.textContent =
          'Porch Pickup has ended';
      }

      if (this.message) {
        this.message.textContent =
          message;
      }

      if (this.keepButton) {
        this.keepButton.textContent =
          'Keep my cart';
      }

      if (this.switchButton) {
        this.switchButton.textContent =
          'Remove expired items & continue';
      }

      this.clearDialogError();
      this.setDialogLoading(false);

      return new Promise((resolve) => {
        this.pendingResolve = resolve;

        if (
          typeof this.dialog.showModal ===
          'function'
        ) {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute(
            'open',
            ''
          );
        }

        window.requestAnimationFrame(
          () => {
            this.keepButton?.focus();
          }
        );
      });
    }

    isPorchPickupLock(item, cart) {
      if (!this.isPickupLock(item)) {
        return false;
      }

      const properties =
        item?.properties || {};

      const lockHandle = String(
        properties
          ._cold_sugar_fulfillment_option ||
        ''
      )
        .trim()
        .toLowerCase();

      if (
        lockHandle.includes('porch')
      ) {
        return true;
      }

      const attributes =
        cart?.attributes || {};

      const cartPath =
        this.normalizeShoppingPath(
          attributes['Shopping Path']
        );

      const cartHandle = String(
        attributes[
          'Fulfillment Option Handle'
        ] || ''
      )
        .trim()
        .toLowerCase();

      return Boolean(
        this.isPorchPath(cartPath) &&
        (
          !lockHandle ||
          !cartHandle ||
          lockHandle === cartHandle
        )
      );
    }

    async removeExpiredPorchItems(
      cart,
      expired
    ) {
      const updates = {};

      expired.items.forEach((item) => {
        if (item.key) {
          updates[item.key] = 0;
        }
      });

      const hasRemainingPorchItems =
        expired.remainingPorchItems
          .length > 0;

      if (!hasRemainingPorchItems) {
        (cart?.items || [])
          .filter((item) =>
            this.isPorchPickupLock(
              item,
              cart
            )
          )
          .forEach((item) => {
            if (item.key) {
              updates[item.key] = 0;
            }
          });
      }

      const remainingVisibleItems =
        (cart?.items || []).filter(
          (item) =>
            !this.isPickupLock(item) &&
            !Object.prototype
              .hasOwnProperty.call(
                updates,
                item.key
              )
        );

      const shouldClearAttributes =
        !hasRemainingPorchItems &&
        (
          this.isPorchPath(
            expired.cartPath
          ) ||
          remainingVisibleItems.length ===
            0
        );

      const payload = {
        updates,
      };

      if (shouldClearAttributes) {
        const attributes = {};

        STALE_FULFILLMENT_ATTRIBUTES
          .forEach((name) => {
            attributes[name] = '';
          });

        payload.attributes =
          attributes;
      }

      const response = await fetch(
        this.route('cart/update.js'),
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type':
              'application/json',
            'X-Requested-With':
              'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        }
      );

      const result =
        await response
          .json()
          .catch(() => ({}));

      if (
        !response.ok ||
        result.errors ||
        result.status
      ) {
        throw new Error(
          result.description ||
          result.message ||
          'The expired Porch Pickup items could not be removed.'
        );
      }

      const updatedCart =
        Array.isArray(result.items)
          ? result
          : await this.fetchCart();

      if (!hasRemainingPorchItems) {
        try {
          const stored =
            JSON.parse(
              sessionStorage.getItem(
                'coldSugarShoppingContext'
              ) || '{}'
            );

          if (
            this.isPorchPath(
              stored.shoppingPath
            )
          ) {
            sessionStorage.removeItem(
              'coldSugarShoppingContext'
            );
          }
        } catch (error) {
          // Session storage is optional.
        }
      }

      this.updateVisibleCartCount(
        updatedCart
      );

      document.dispatchEvent(
        new CustomEvent(
          'cold-sugar:expired-porch-removed',
          {
            bubbles: true,
            detail: {
              cart: updatedCart,
            },
          }
        )
      );

      document.dispatchEvent(
        new CustomEvent(
          'cart:updated',
          {
            bubbles: true,
            detail: {
              cart: updatedCart,
            },
          }
        )
      );

      return updatedCart;
    }

    updateVisibleCartCount(cart) {
      const count =
        (cart?.items || []).reduce(
          (total, item) =>
            this.isPickupLock(item)
              ? total
              : (
                  total +
                  Number(
                    item.quantity || 0
                  )
                ),
          0
        );

      document
        .querySelectorAll(
          '[data-cart-count], .cart-count-bubble span[aria-hidden="true"]'
        )
        .forEach((node) => {
          node.textContent =
            String(count);
        });

      document
        .querySelectorAll(
          '.cart-count-bubble'
        )
        .forEach((bubble) => {
          bubble.classList.toggle(
            'hidden',
            count === 0
          );
        });
    }

    async getCartMode() {
      const cart = await this.fetchCart();
      return this.determineCartMode(cart);
    }

    determineCartMode(cart) {
      const modes = new Set();
      let hasUnknownRealItem = false;

      (cart?.items || []).forEach((item) => {
        if (this.isPickupLock(item)) return;

        const mode = this.normalizeMode(
          item.properties && item.properties._cold_sugar_order_mode
        );

        if (mode) {
          modes.add(mode);
        } else {
          hasUnknownRealItem = true;
        }
      });

      if (modes.size === 0 && !hasUnknownRealItem) return 'empty';
      if (modes.size > 1 || hasUnknownRealItem) return 'mixed';

      return Array.from(modes)[0];
    }

    isPickupLock(item) {
      const value = item?.properties?._cold_sugar_pickup_lock;

      return (
        value === true ||
        value === 'true' ||
        value === 1 ||
        value === '1' ||
        item?.handle === 'pickup-location'
      );
    }

    requestModeSwitch({ cartMode, requestedMode, trigger }) {
      if (!this.dialog) {
        return Promise.resolve(
          window.confirm(
            'Local and shipped items require separate orders. Clear the current cart and start a new order?'
          )
        );
      }

      if (this.pendingResolve) {
        this.finishDecision(false);
      }

      this.lastFocusedElement =
        trigger instanceof HTMLElement ? trigger : document.activeElement;

      this.setDialogCopy(cartMode, requestedMode);
      this.clearDialogError();
      this.setDialogLoading(false);

      return new Promise((resolve) => {
        this.pendingResolve = resolve;

        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        window.requestAnimationFrame(() => {
          this.keepButton?.focus();
        });
      });
    }

    setDialogCopy(cartMode, requestedMode) {
      if (!this.title || !this.message || !this.keepButton || !this.switchButton) {
        return;
      }

      if (requestedMode === 'shipping' && cartMode === 'local') {
        this.title.textContent = 'Start a shipped order?';
        this.message.textContent =
          'Your cart currently contains items for local pickup or delivery. Shipped and local items must be placed as separate orders.';
        this.keepButton.textContent = 'Keep my local order';
        this.switchButton.textContent = 'Clear cart & start shipped order';
        return;
      }

      if (requestedMode === 'local' && cartMode === 'shipping') {
        this.title.textContent = 'Start a local order?';
        this.message.textContent =
          'Your cart currently contains items that ship. Local pickup and delivery items must be placed as a separate order.';
        this.keepButton.textContent = 'Keep my shipped order';
        this.switchButton.textContent = 'Clear cart & start local order';
        return;
      }

      this.title.textContent =
        requestedMode === 'shipping'
          ? 'Start a shipped order?'
          : 'Start a local order?';

      this.message.textContent =
        'Your cart contains items whose fulfillment type cannot be combined with this product. Clear the cart to begin a separate order.';

      this.keepButton.textContent = 'Keep my current order';
      this.switchButton.textContent =
        requestedMode === 'shipping'
          ? 'Clear cart & start shipped order'
          : 'Clear cart & start local order';
    }

    bindDialog() {
      if (!this.dialog) return;

      this.keepButton?.addEventListener('click', () => {
        if (!this.isSwitching) this.finishDecision(false);
      });

      this.closeButton?.addEventListener('click', () => {
        if (!this.isSwitching) this.finishDecision(false);
      });

      this.switchButton?.addEventListener('click', () => {
        if (!this.isSwitching) this.finishDecision(true);
      });

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        if (!this.isSwitching) this.finishDecision(false);
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog && !this.isSwitching) {
          this.finishDecision(false);
        }
      });

      this.dialog.addEventListener('close', () => {
        this.restoreFocus();
      });
    }

    finishDecision(value) {
      if (!this.pendingResolve) return;

      const resolve = this.pendingResolve;
      this.pendingResolve = null;

      if (typeof this.dialog?.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog?.removeAttribute('open');
        this.restoreFocus();
      }

      resolve(value);
    }

    setDialogLoading(isLoading) {
      this.isSwitching = isLoading;

      [this.keepButton, this.switchButton, this.closeButton].forEach((button) => {
        if (button) button.disabled = isLoading;
      });
    }

    showDialogError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearDialogError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }

    restoreFocus() {
      if (this.lastFocusedElement instanceof HTMLElement) {
        this.lastFocusedElement.focus();
      }

      this.lastFocusedElement = null;
    }

    async clearCartAndFulfillment() {
      this.setDialogLoading(true);
      this.clearDialogError();

      try {
        const clearResponse = await fetch(this.route('cart/clear.js'), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({}),
        });

        if (!clearResponse.ok) {
          throw new Error('We could not clear the current cart.');
        }

        const attributes = {};
        STALE_FULFILLMENT_ATTRIBUTES.forEach((name) => {
          attributes[name] = '';
        });

        const updateResponse = await fetch(this.route('cart/update.js'), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ attributes }),
        });

        if (!updateResponse.ok) {
          throw new Error('We could not reset the previous fulfillment selection.');
        }

        document.dispatchEvent(
          new CustomEvent('cold-sugar:order-mode-reset', {
            bubbles: true,
          })
        );
      } catch (error) {
        this.showDialogError(
          error.message || 'We could not start the new order. Please try again.'
        );
        throw error;
      } finally {
        this.setDialogLoading(false);
      }
    }

    async fetchCart() {
      const response = await fetch(this.route('cart.js'), {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error('We could not read your cart. Please try again.');
      }

      return response.json();
    }

    route(path) {
      const root =
        (window.Shopify &&
          window.Shopify.routes &&
          window.Shopify.routes.root) ||
        '/';

      return `${root.replace(/\/?$/, '/')}${String(path).replace(/^\//, '')}`;
    }
  }

  const initialize = () => {
    if (!window.ColdSugarOrderMode) {
      window.ColdSugarOrderMode = new ColdSugarOrderModeController();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
